/* ExpensesCrudCard.tsx
    Une card affichant la gestion des dépenses
    Utilise le composant GenericCrudCard pour lister, ajouter, gérer et supprimer les éléments
    Inclut la facture fournisseur liée en colonne visuelle et un export CSV enrichi
*/

import React from 'react';
import { GenericCrudCard } from 'common';
import { ExpenseData, expensesApi } from 'features/expenses';
import { faReceipt } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

// Traduction des types de dépense
const EXPENSE_TYPE_LABELS: Record<string, string> = {
	FREE: 'Libre',
	CONTRACT: 'Contrat',
	ORDER: 'Commande',
	PROJECT: 'Affaire',
};

/** Formatte un montant en euros (fr-FR) */
const formatCurrency = (value: number | null | undefined): string =>
	new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(
		Number(value ?? 0)
	);

interface ExpensesCrudCardProps {
	size?: 'sm' | 'md' | 'lg';
	returnPath?: string;
	returnLabel?: string;
	returnIcon?: IconDefinition;
	returnInvert?: boolean;
	className?: string;
	/** Affiche ou masque l'icône dans l'en-tête de la card. Défaut : true */
	showIcon?: boolean;
	/** Masque le bouton retour. Utile quand la card est intégrée dans une page avec son propre bouton retour. Défaut : false */
	hideReturn?: boolean;
}

// ExpenseCrudCard component
export const ExpensesCrudCard: React.FC<ExpensesCrudCardProps> = ({
	size = 'sm',
	returnPath = '/app/expenses',
	returnLabel,
	returnIcon,
	returnInvert = false,
	className,
	showIcon = true,
	hideReturn = false,
}) => {
	// Détermination si on utilise la version étendue (size !== 'sm')
	const extended = size !== 'sm';

	// Définition des colonnes du tableau
	const columns = [
		// Colonne "ID" uniquement si extended
		...(extended
			? [{ label: 'ID', field: 'id', render: (a: ExpenseData) => a.id }]
			: []),
		// Colonne "Date de dépense" au format jour/mois/année
		{
			label: 'Date',
			field: 'expenseDate',
			filterType: 'date',
			render: (a: ExpenseData) =>
				a.expenseDate
					? new Date(a.expenseDate).toLocaleDateString('fr-FR')
					: '—',
			exportValue: (a: ExpenseData): string =>
				a.expenseDate
					? new Date(a.expenseDate).toLocaleDateString('fr-FR')
					: '',
		},
		// Colonne "Libellé"
		{
			label: 'Libellé',
			field: 'label',
			render: (a: ExpenseData) => a.label,
			exportValue: (a: ExpenseData): string => a.label ?? '',
		},
		// Colonne du montant facturé HT
		{
			label: 'Facturé HT',
			field: 'amountHtInvoiced',
			render: (a: ExpenseData) => formatCurrency(a.amountHtInvoiced),
			exportValue: (a: ExpenseData): number => Number(a.amountHtInvoiced ?? 0),
		},
		// Colonne "Centre de coût" uniquement si extended
		...(extended
			? [
					{
						label: 'Centre de coût',
						field: 'costCenter.label',
						render: (a: ExpenseData) => a.costCenter?.label ?? '—',
						exportValue: (a: ExpenseData): string => a.costCenter?.label ?? '',
					},
				]
			: []),
		// Colonne "Affaire" liée à la dépense — affichée si extended
		...(extended
			? [
					{
						label: 'Affaire',
						field: 'project.referenceNumber',
						render: (a: ExpenseData) => a.project?.referenceNumber ?? '—',
						exportValue: (a: ExpenseData): string =>
							a.project?.referenceNumber ?? '',
						linkUrl: (a: ExpenseData) =>
							a.project?.id ? `/app/projects/${a.project.id}/edit` : '',
					},
				]
			: []),
		// Colonne "Fournisseur" uniquement si extended
		...(extended
			? [
					{
						label: 'Fournisseur',
						field: 'supplier.label',
						render: (a: ExpenseData) => a.supplier?.label ?? '—',
						exportValue: (a: ExpenseData): string => a.supplier?.label ?? '',
						linkUrl: (a: ExpenseData) =>
							a.supplier?.id ? `/app/tiers/${a.supplier.id}/manage` : '',
					},
				]
			: []),
		// Colonne "Facture fournisseur" uniquement si extended — lien vers la fiche facture
		...(extended
			? [
					{
						label: 'Facture fourn.',
						field: 'supplierInvoice.referenceNumber',
						render: (a: ExpenseData) =>
							a.supplierInvoice?.referenceNumber ?? '—',
						exportValue: (a: ExpenseData): string =>
							a.supplierInvoice?.referenceNumber ?? '',
						linkUrl: (a: ExpenseData) =>
							a.supplierInvoice?.id
								? `/app/supplierInvoices/${a.supplierInvoice.id}/edit`
								: '',
					},
				]
			: []),
	];

	// Colonnes visibles uniquement dans l'export Excel
	const exportOnlyColumns = [
		// Type de dépense (FREE, CONTRACT, ORDER, PROJECT → traduit)
		{
			label: 'Type',
			exportValue: (a: ExpenseData): string =>
				EXPENSE_TYPE_LABELS[a.type ?? ''] ?? a.type ?? '',
		},
		// Montant HT prévu (budget)
		{
			label: 'Montant HT prévu',
			exportValue: (a: ExpenseData): number | string =>
				a.amountHtExpected != null ? Number(a.amountHtExpected) : '',
		},
		// Envoi en comptabilité
		{
			label: 'Envoyé en compta',
			exportValue: (a: ExpenseData): string =>
				a.sentToAccounting ? 'Oui' : 'Non',
		},
		// Contact interne responsable
		{
			label: 'Contact interne',
			exportValue: (a: ExpenseData): string => {
				const u = a.internalContact;
				if (!u) return '';
				return [u.firstName, u.name].filter(Boolean).join(' ');
			},
		},
		// Créé par
		{
			label: 'Créé par',
			exportValue: (a: ExpenseData): string => {
				const u = a.createdBy;
				if (!u) return '';
				return [u.firstName, u.name].filter(Boolean).join(' ');
			},
		},
		// Créé le
		{
			label: 'Créé le',
			exportValue: (a: ExpenseData): string =>
				a.createdAt ? new Date(a.createdAt).toLocaleDateString('fr-FR') : '',
		},
		// En mode sm, le fournisseur n'est pas affiché → l'ajouter dans l'export
		...(!extended
			? [
					{
						label: 'Fournisseur',
						exportValue: (a: ExpenseData): string => a.supplier?.label ?? '',
					},
					{
						label: 'Facture fournisseur',
						exportValue: (a: ExpenseData): string =>
							a.supplierInvoice?.referenceNumber ?? '',
					},
				]
			: []),
	];

	return (
		<GenericCrudCard<ExpenseData>
			queryKey="expenses"
			title={size === 'sm' ? 'Dépenses' : 'Liste des dépenses'}
			fetchData={(token, options) => expensesApi.listPaginated(token, options)}
			deleteItem={expensesApi.delete}
			tableColumns={columns}
			exportOnlyColumns={exportOnlyColumns}
			addButtonLabel="Ajouter une dépense"
			getAddPath={() => '/app/expenses/new'}
			getEditPath={(id) => `/app/expenses/${id}/edit`}
			emptyMessage="Aucune dépense trouvée."
			getReturnPath={hideReturn ? undefined : () => returnPath}
			returnLabel={returnLabel}
			returnIcon={returnIcon}
			returnInvert={returnInvert}
			size={size}
			icon={size === 'sm' ? faReceipt : undefined}
			className={className}
			showIcon={showIcon}
		/>
	);
};
export default ExpensesCrudCard;
